import { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  TransferList,
  Button,
  Group,
  Divider,
  Box,
  TextInput,
  Text,
} from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { ArrowNarrowLeft } from "tabler-icons-react";

import {
  Role,
  useRoleFunctionsQuery,
  useRoleQuery,
  useUpdateRoleFunctionsMutation,
  useUpdateRoleMutation,
  useUpdateRoleUsersMutation,
  roleSchema,
} from "services/api";
import { FunctionList } from "./FunctionList";
import useUserTransferList from "./useUserTransferList";

const Edit = () => {
  const form = useForm<Role>({
    validate: zodResolver(roleSchema),
    initialValues: {
      roleNo: "",
      roleName: "",
    },
  });

  const { id } = useParams();
  const { data: role } = useRoleQuery(id, { skip: id === undefined });

  useEffect(() => {
    if (role === undefined) {
      return;
    }

    form.setValues({
      roleNo: role.roleNo ?? "",
      roleName: role.roleName ?? "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  const [userTransferList, setUserTransferList, isLoadingUserTransferList] =
    useUserTransferList(id);

  const [functionList, setFunctionList] = useState<string[]>([]);

  const { data: roleFunctions, isLoading: isLoadingRoleFunctions } =
    useRoleFunctionsQuery(id);

  useEffect(() => {
    if (!roleFunctions) {
      return;
    }

    setFunctionList(roleFunctions.map(String));
  }, [roleFunctions]);

  const [updateRole, { isLoading: isUpdatingRole }] = useUpdateRoleMutation();

  const [updateRoleFunctions, { isLoading: isUpdatingRoleFunctions }] =
    useUpdateRoleFunctionsMutation();
  const [updateRoleUsers, { isLoading: isUpdatingRoleUsers }] =
    useUpdateRoleUsersMutation();

  const navigate = useNavigate();

  const handleSubmit = async (values: Role) => {
    try {
      await Promise.all([
        updateRole(values).unwrap(),
        updateRoleFunctions({
          id: values.roleNo,
          functions: functionList.map(Number),
        }).unwrap(),
        updateRoleUsers({
          id: values.roleNo,
          users: userTransferList[0].map((x) => x.value),
        }).unwrap(),
      ]);

      navigate("../roles", { replace: true });
    } catch (e) {}
  };

  return (
    <Box py={8} px={36}>
      <Text
        variant="link"
        component={Link}
        color="cyan"
        to="../roles"
        underline={false}
        sx={(theme) => ({
          display: "inline-flex",
          alignItems: "center",
          "&:hover": {
            borderBottom: "1px solid" + theme.colors.cyan[6],
          },
        })}
      >
        <ArrowNarrowLeft size={20} /> 返回上一頁
      </Text>
      <h2>群組角色設定</h2>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Box>
          <TextInput
            required
            label="角色名稱"
            placeholder="系統管理員"
            {...form.getInputProps("roleName")}
            limit={10}
          />
        </Box>

        <Divider my="lg" label="設定使用者" />

        <TransferList
          value={userTransferList}
          onChange={setUserTransferList}
          searchPlaceholder="搜尋姓名或部門關鍵字"
          nothingFound="沒有找到任何人員"
          titles={["已設定人員", "未設定人員"]}
          limit={1000}
          breakpoint="sm"
        />

        <Divider my="lg" label="設定角色所能執行功能" />

        <FunctionList value={functionList} onChange={setFunctionList} />

        <Group mt="xl">
          <Button
            type="submit"
            color="cyan"
            disabled={isLoadingUserTransferList || isLoadingRoleFunctions}
            loading={
              isUpdatingRole || isUpdatingRoleFunctions || isUpdatingRoleUsers
            }
          >
            更新資料
          </Button>
        </Group>
      </form>
    </Box>
  );
};

export default Edit;
